/** Strip HTML tags for previews, search, and card excerpts. */
export function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function looksLikeHtml(value) {
  return /<[^>]+>/.test(String(value || ""));
}

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Title words may be wrapped in tags inside the first paragraph. */
function buildFlexibleTitlePattern(title) {
  const parts = title.trim().split(/\s+/).filter(Boolean).map(escapeRegex);
  if (!parts.length) return null;
  return parts.join("(?:\\s|<[^>]*>\\s*)*");
}

function stripTitleLeadFromHtml(html, title) {
  const flexible = buildFlexibleTitlePattern(title);
  if (!flexible) return html;

  const leadInFirstP = new RegExp(
    `(<p[^>]*>)(?:\\s|<[^>]+>)*${flexible}(?:\\s|<[^>]+>)*\\s*[:\\-–—]+\\s*`,
    "i"
  );
  if (leadInFirstP.test(html)) {
    return html.replace(leadInFirstP, "$1");
  }

  const titleOnlyP = new RegExp(
    `^\\s*<p[^>]*>(?:\\s|<[^>]+>)*${flexible}(?:\\s|<[^>]+>)*\\s*<\\/p>\\s*`,
    "i"
  );
  if (titleOnlyP.test(html)) {
    const cleaned = html.replace(titleOnlyP, "").trim();
    return cleaned || html;
  }

  return html;
}

/** Remove a leading "Title :-" / "Title -" line while preserving HTML from the CMS. */
export function stripServiceDescriptionLead(description, title) {
  if (!description?.trim()) return "";
  if (!title?.trim()) return description;

  if (looksLikeHtml(description)) {
    return stripTitleLeadFromHtml(description, title);
  }

  const lines = String(description).split("\n");
  const escaped = escapeRegex(title.trim());
  const leadPattern = new RegExp(`^\\s*${escaped}\\s*[:\\-–—]+\\s*`, "i");

  if (lines.length > 1 && leadPattern.test(lines[0])) {
    const rest = lines.slice(1).join("\n").trim();
    if (rest) return rest;
  }

  if (leadPattern.test(lines[0])) {
    const rest = lines[0].replace(leadPattern, "").trim();
    const following = lines.slice(1).join("\n").trim();
    return [rest, following].filter(Boolean).join("\n");
  }

  return description;
}

/** Remove list markup/lines so bullets can be shown separately. */
export function stripListsFromContent(content) {
  if (!content?.trim()) return "";

  if (looksLikeHtml(content)) {
    return content
      .replace(/<ul[^>]*>[\s\S]*?<\/ul>/gi, "")
      .replace(/<ol[^>]*>[\s\S]*?<\/ol>/gi, "")
      .trim();
  }

  return String(content)
    .split("\n")
    .filter((line) => {
      const trimmed = line.trim();
      return trimmed && !trimmed.startsWith("•") && !trimmed.startsWith("-") && !trimmed.startsWith("*");
    })
    .join("\n")
    .trim();
}

/** Plain intro text for service cards — first paragraph only, no bullet lists. */
export function getServicePreviewText(description, title) {
  const body = stripServiceDescriptionLead(description, title);
  if (!body?.trim()) return "";

  if (looksLikeHtml(body)) {
    const withoutLists = body
      .replace(/<ul[^>]*>[\s\S]*?<\/ul>/gi, "")
      .replace(/<ol[^>]*>[\s\S]*?<\/ol>/gi, "");

    const firstP = withoutLists.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
    if (firstP) {
      const text = stripHtml(firstP[1]).replace(/\s+/g, " ").trim();
      if (text) return text;
    }

    return stripHtml(withoutLists).replace(/\s+/g, " ").trim();
  }

  const lines = String(body).split("\n");
  const intro = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith("•") || trimmed.startsWith("-") || trimmed.startsWith("*")) break;
    intro.push(trimmed);
  }
  return intro.join(" ").trim();
}

/** Short excerpt for cards/hero — skips title line and returns first meaningful paragraph. */
export function getServiceExcerpt(description, title, maxLength = 200) {
  const body = stripServiceDescriptionLead(description, title);
  const plain = stripHtml(body).replace(/\s+/g, " ").trim();
  if (!plain) return "";

  const normalizedTitle = stripHtml(title).trim().toLowerCase();
  if (plain.toLowerCase() === normalizedTitle) return "";

  if (plain.length <= maxLength) return plain;
  const cut = plain.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

/** Extract bullet lines from plain text or HTML list items. */
export function extractBullets(text) {
  if (!text) return [];

  if (looksLikeHtml(text)) {
    const items = [...String(text).matchAll(/<li[^>]*>(.*?)<\/li>/gis)].map((m) =>
      stripHtml(m[1])
    );
    if (items.length) return items.filter(Boolean);
    return [];
  }

  return String(text)
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("•") || /^Engineering-led|^Premium materials|^On-time/.test(line))
    .map((line) => line.replace(/^•\s*/, ""));
}
